import { useLayoutEffect } from 'react';
import type React from 'react';

import { useLocation } from 'react-router';

import { isDarkModeExcludedPath } from '#src/shared/darkMode';

import { useSessionConfiguration } from '#src/app/shared/hooks/sessionConfiguration';
import { addMediaQueryListener, removeMediaQueryListener } from '#src/app/shared/utils/dom';

/**
 * Client-side navigation doesn't reload the page, so the `.dark` class applied by the
 * bootstrap dark-mode script (config/app-index.ts) to a prior page can persist onto a
 * page reached via an in-app link that should be excluded from dark mode, since that
 * script only reacts to full page loads and OS `prefers-color-scheme` changes, never to
 * route changes. This reconciles the class on every route change instead — using
 * useLayoutEffect, not useEffect, so the class is corrected before the new route paints.
 *
 * It also subscribes to OS `prefers-color-scheme` changes itself, so an OS theme change is
 * applied immediately even if the HTML shell didn't include the bootstrap script. Otherwise the
 * change would only be picked up on the next client-side navigation, flipping the theme
 * seemingly at random.
 *
 * Also runs on initial mount/hydration (useLayoutEffect fires then too), so it must gate on
 * the same condition as the bootstrap script's darkModeEnabled prop (ssr/render.tsx and
 * csr/genIndex.ts) — the FCM_DARK_MODE_ENABLED kill-switch.
 */
export const DarkModeExclusion: React.FC<EmptyProps> = () => {
	const { pathname } = useLocation();
	const {
		featureConfigs: { darkModeEnabled },
	} = useSessionConfiguration();

	useLayoutEffect(() => {
		if (!darkModeEnabled) return undefined;

		const rootElement = document.documentElement;
		const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

		const applyTheme = () => {
			const isDark = darkModeMediaQuery.matches && !isDarkModeExcludedPath(pathname);

			rootElement.classList.toggle('dark', isDark);
			rootElement.style.colorScheme = isDark ? 'dark' : 'light';
		};

		applyTheme();
		addMediaQueryListener(darkModeMediaQuery, applyTheme);
		return () => removeMediaQueryListener(darkModeMediaQuery, applyTheme);
	}, [pathname, darkModeEnabled]);

	return null;
};
